import { useState, useRef, useEffect } from 'react'
import { Icon, ICONS } from './Icon'

// A compact "Label (n) ▾" button that opens a checkbox list — the same multi-select
// filter a row of standalone checkboxes gives, without permanently taking up a full row
// of horizontal space for a filter most visits leave untouched.
export function MultiSelectDropdown<T extends string>({
  label, options, selected, onToggle, onClear,
}: {
  label: string
  options: { key: T; label: string }[]
  selected: Set<T>
  onToggle: (key: T) => void
  onClear?: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '9px 14px', borderRadius: 9, cursor: 'pointer',
          background: selected.size > 0 ? 'rgba(15,124,108,0.08)' : '#fff',
          border: `1px solid ${selected.size > 0 ? 'rgba(15,124,108,0.3)' : 'rgba(27,42,74,0.15)'}`,
          font: '500 13px Inter, sans-serif', color: selected.size > 0 ? '#0F7C6C' : '#1B2A4A',
        }}
      >
        {label}{selected.size > 0 && ` (${selected.size})`}
        <Icon path={open ? ICONS.chevronUp : ICONS.chevronDown} size={11} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 20, minWidth: 190,
          background: '#fff', border: '1px solid rgba(27,42,74,0.1)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(27,42,74,0.12)', padding: '8px 6px',
        }}>
          {options.map(o => (
            <label
              key={o.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px',
                borderRadius: 7, cursor: 'pointer', font: '400 13px Inter, sans-serif', color: '#1B2A4A',
              }}
            >
              <input type='checkbox' checked={selected.has(o.key)} onChange={() => onToggle(o.key)} />
              {o.label}
            </label>
          ))}
          {onClear && selected.size > 0 && (
            <button
              onClick={onClear}
              style={{
                width: '100%', textAlign: 'left', marginTop: 4, padding: '7px 8px',
                background: 'none', border: 'none', borderTop: '1px solid rgba(27,42,74,0.06)',
                font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}
